import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { NavLink } from "react-router-dom";
import Layout from "../../components/Layout";
import Card from "../../components/ui/Card";
import DataTable, {
  Column,
  DataTableFetchParams,
  DataTableFilterDef,
} from "../../components/ui/DataTable";
import Button from "../../components/ui/Button";
import Dialog from "../../components/ui/Dialog";
import Input from "../../components/ui/Input";
import DropzoneFileInput from "../../components/ui/DropzoneFileInput";
import { useAuth } from "../../context/AuthContext";
import { Permission } from "../../permissions/permission.enum";
import { cardInterestsService } from "../../services/card-interests.service";
import { jobRunsService } from "../../services/job-runs.service";
import { CardOfInterest } from "../../types/card-interest";

const CardInterestsList = () => {
  const { t } = useTranslation("common");
  const { hasPermission } = useAuth();

  const [tableRefreshKey, setTableRefreshKey] = useState(0);
  const [actionMessage, setActionMessage] = useState<string>("");
  const [lastRunSourceId, setLastRunSourceId] = useState<string | null>(null);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<CardOfInterest | null>(null);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [submitError, setSubmitError] = useState<string>("");
  const [referenceFile, setReferenceFile] = useState<File | null>(null);

  const [form, setForm] = useState<{
    name: string;
    keywords: string;
    locale: string;
    enabled: boolean;
  }>({
    name: "",
    keywords: "",
    locale: "en",
    enabled: true,
  });

  const canCreate = hasPermission(Permission.CARD_INTERESTS_CREATE);
  const canUpdate = hasPermission(Permission.CARD_INTERESTS_UPDATE);
  const canDelete = hasPermission(Permission.CARD_INTERESTS_DELETE);

  const [runningJobs, setRunningJobs] = useState<Record<string, string>>({});
  const pollIntervalsRef = useRef<Record<string, ReturnType<typeof setInterval>>>(
    {},
  );

  useEffect(() => {
    return () => {
      Object.values(pollIntervalsRef.current).forEach(clearInterval);
    };
  }, []);

  const startPolling = (sourceId: string, jobId: string) => {
    if (pollIntervalsRef.current[sourceId]) return;
    pollIntervalsRef.current[sourceId] = setInterval(async () => {
      try {
        const job = await jobRunsService.getOne(jobId);
        if (job.status !== "running") {
          clearInterval(pollIntervalsRef.current[sourceId]);
          delete pollIntervalsRef.current[sourceId];
          setRunningJobs((prev) => {
            const next = { ...prev };
            delete next[sourceId];
            return next;
          });
          const summary: any = job.summary || {};
          const details = [
            `Status: ${job.status}`,
            `cards saved: ${summary.cardsSaved ?? 0}`,
            `stored: ${summary.stored ?? 0}`,
            `errors: ${summary.errors ?? 0}`,
          ].join(" | ");
          setActionMessage(`Job ${job.id} complete — ${details}`);
          setLastRunSourceId(sourceId);
          setTableRefreshKey((prev) => prev + 1);
        }
      } catch {
        // swallow poll errors
      }
    }, 5000);
  };

  const handleFetch = useCallback(async (params: DataTableFetchParams) => {
    const enabledValue = params.filters?.enabled;
    const parsedEnabled =
      enabledValue === "true"
        ? true
        : enabledValue === "false"
          ? false
          : undefined;

    const res = await cardInterestsService.list({
      page: params.page,
      take: params.pageSize,
      orderBy: params.sortBy || "createdAt",
      order: params.sortOrder || "DESC",
      search: params.search || undefined,
      enabled: parsedEnabled,
    });

    return {
      data: res.data,
      total: res.meta.itemCount,
      totalPages: res.meta.pageCount,
    };
  }, []);

  const runNow = async (sourceId: string) => {
    setActionMessage("");
    setLastRunSourceId(null);
    try {
      const jobRun = await cardInterestsService.runNow(sourceId);
      if (jobRun.status === "running") {
        setRunningJobs((prev) => ({ ...prev, [sourceId]: jobRun.id }));
        setActionMessage(`Job ${jobRun.id} started — running in background…`);
        setLastRunSourceId(sourceId);
        startPolling(sourceId, jobRun.id);
      } else {
        const summary: any = jobRun.summary || {};
        setActionMessage(
          `Job ${jobRun.id} — status: ${jobRun.status} | ${jobRun.errorDetails ?? JSON.stringify(summary)}`,
        );
        setLastRunSourceId(sourceId);
        setTableRefreshKey((prev) => prev + 1);
      }
    } catch (e: any) {
      setActionMessage(e?.message || "Failed to start job");
    }
  };

  const openCreate = () => {
    setEditing(null);
    setSubmitError("");
    setReferenceFile(null);
    setForm({
      name: "",
      keywords: "",
      locale: "en",
      enabled: true,
    });
    setDialogOpen(true);
  };

  const openEdit = (interest: CardOfInterest) => {
    setEditing(interest);
    setSubmitError("");
    setReferenceFile(null);
    setForm({
      name: interest.name || "",
      keywords: interest.keywords || "",
      locale: interest.locale || "en",
      enabled: interest.enabled ?? true,
    });
    setDialogOpen(true);
  };

  const closeDialog = () => {
    if (submitLoading) return;
    setDialogOpen(false);
  };

  const onSubmit = async () => {
    setSubmitError("");
    const name = form.name.trim();
    const keywords = form.keywords.trim();
    if (!name) {
      setSubmitError(t("name_required"));
      return;
    }
    if (!keywords) {
      setSubmitError(t("keywords_required"));
      return;
    }
    if (name.toLowerCase() !== keywords.toLowerCase()) {
      setSubmitError(t("keywords_should_match_name"));
      return;
    }
    if (!editing && !referenceFile) {
      setSubmitError(t("reference_image_required"));
      return;
    }

    setSubmitLoading(true);
    try {
      const wasEnabled = editing?.enabled ?? false;
      const payload = {
        name,
        keywords,
        locale: form.locale.trim() || "en",
        enabled: form.enabled,
        imageMatchRequired: true,
      };

      let saved: CardOfInterest;
      if (editing) {
        saved = await cardInterestsService.update(editing.id, payload);
      } else {
        saved = await cardInterestsService.create(payload);
        if (!referenceFile) throw new Error(t("reference_image_required"));
        await cardInterestsService.uploadReferenceImage(saved.id, referenceFile);
      }
      setDialogOpen(false);
      setTableRefreshKey((prev) => prev + 1);
      if (saved.enabled && !wasEnabled) {
        await runNow(saved.id);
      }
    } catch (e: any) {
      setSubmitError(e?.message || "Failed to save card interest");
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm(t("confirmDelete"))) return;
    try {
      await cardInterestsService.remove(id);
      setTableRefreshKey((prev) => prev + 1);
    } catch (e: any) {
      setActionMessage(e?.message || "Failed to delete");
    }
  };

  const toggleEnabled = async (row: CardOfInterest, nextEnabled: boolean) => {
    if (!canUpdate) return;
    setActionMessage("");
    try {
      const updated = await cardInterestsService.update(row.id, {
        enabled: nextEnabled,
      });
      setTableRefreshKey((prev) => prev + 1);
      if (updated.enabled) {
        await runNow(updated.id);
      }
    } catch (e: any) {
      setActionMessage(e?.message || "Failed to update");
    }
  };

  const columns: Column<CardOfInterest>[] = [
    {
      key: "name",
      header: t("name"),
      className: "font-medium text-gray-900 dark:text-gray-100",
      render: (row) => (
        <NavLink
          to={`/card-interests/${row.id}`}
          className="text-blue-600 hover:underline dark:text-blue-400"
        >
          {row.name || row.keywords || row.id}
        </NavLink>
      ),
    },
    {
      key: "keywords",
      header: t("keywords"),
      render: (row) => row.keywords,
    },
    {
      key: "locale",
      header: t("locale"),
      render: (row) => row.locale || "en",
    },
    {
      key: "enabled",
      header: t("enabled"),
      render: (row) => (
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={row.enabled}
            onChange={(e) => toggleEnabled(row, e.target.checked)}
            disabled={!canUpdate || !!runningJobs[row.id]}
          />
          <span className="text-sm">{row.enabled ? t("yes") : t("no")}</span>
        </div>
      ),
    },
    {
      key: "actions",
      header: t("actions"),
      className: "text-right",
      render: (row) => (
        <div className="flex items-center justify-end gap-2">
          {canUpdate && (
            <Button size="sm" variant="secondary" onClick={() => openEdit(row)}>
              {t("edit")}
            </Button>
          )}
          {runningJobs[row.id] ? (
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-md text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-700">
              <span className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-pulse" />
              Running…
            </span>
          ) : (
            <Button size="sm" onClick={() => runNow(row.id)} disabled={!row.enabled}>
              {t("run_now")}
            </Button>
          )}
          {canDelete && (
            <Button
              size="sm"
              variant="danger"
              onClick={() => handleDelete(row.id)}
            >
              {t("delete")}
            </Button>
          )}
        </div>
      ),
    },
  ];

  const filterDefs: DataTableFilterDef[] = [
    {
      key: "enabled",
      label: t("enabled"),
      type: "select",
      options: [
        { label: t("all"), value: "" },
        { label: t("yes"), value: "true" },
        { label: t("no"), value: "false" },
      ],
    },
  ];

  return (
    <Layout title={t("card_interests")}>
      <div className="space-y-4 h-full flex flex-col">
        <Card className="p-4 shadow-sm flex-shrink-0">
          <div className="flex flex-col gap-3">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
              <h1 className="text-2xl font-semibold tracking-tight">
                {t("card_interests")}
              </h1>
              {canCreate && (
                <div className="flex items-center gap-2">
                  <Button onClick={openCreate}>+ {t("add_card_interest")}</Button>
                </div>
              )}
            </div>
            {actionMessage && (
              <div className="p-3 rounded-md bg-gray-50 text-gray-700 border border-gray-200 dark:bg-gray-800 dark:text-gray-200 dark:border-gray-700">
                {actionMessage}
              </div>
            )}
            {lastRunSourceId && (
              <div className="text-xs text-gray-500 dark:text-gray-400">
                sourceId: {lastRunSourceId}
              </div>
            )}
          </div>
        </Card>

        <DataTable
          className="flex-1 min-h-0"
          columns={columns}
          onFetch={handleFetch}
          filterDefs={filterDefs}
          initialState={{
            sortBy: "createdAt",
            sortOrder: "DESC",
            pageSize: 50,
          }}
          refreshKey={tableRefreshKey}
          emptyMessage={t("noData")}
        />
      </div>

      <Dialog
        open={dialogOpen}
        onClose={closeDialog}
        title={editing ? t("edit_card_interest") : t("add_card_interest")}
      >
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            onSubmit();
          }}
        >
          {submitError && (
            <div className="p-3 rounded-md bg-red-50 text-red-600 border border-red-100">
              {submitError}
            </div>
          )}

          <Input
            label={t("name")}
            value={form.name}
            onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
            placeholder={t("name_placeholder")}
            disabled={submitLoading}
          />

          <Input
            label={t("keywords")}
            value={form.keywords}
            onChange={(e) =>
              setForm((prev) => ({ ...prev, keywords: e.target.value }))
            }
            placeholder={t("keywords_match_name_hint")}
            disabled={submitLoading}
          />

          {!editing && (
            <DropzoneFileInput
              label={`${t("reference_image")} (${t("optional")})`}
              helperText={t("uploader_image_hint")}
              value={referenceFile}
              onChange={setReferenceFile}
              accept="image/*"
              disabled={submitLoading}
              maxBytes={5 * 1024 * 1024}
            />
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                {t("locale")}
              </label>
              <select
                className="w-full rounded-md border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={form.locale}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, locale: e.target.value }))
                }
                disabled={submitLoading}
              >
                <option value="en">en</option>
                <option value="ja">ja</option>
              </select>
            </div>

            <div className="flex items-center gap-3 pt-6">
              <input
                id="interest-enabled"
                type="checkbox"
                className="h-4 w-4"
                checked={form.enabled}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, enabled: e.target.checked }))
                }
                disabled={submitLoading}
              />
              <label
                htmlFor="interest-enabled"
                className="text-sm text-gray-700 dark:text-gray-300"
              >
                {t("background_scrape")}
              </label>
            </div>
          </div>

          <div className="flex items-center justify-end gap-2">
            <Button
              type="button"
              variant="secondary"
              onClick={closeDialog}
              disabled={submitLoading}
            >
              {t("cancel")}
            </Button>
            <Button type="submit" isLoading={submitLoading}>
              {t("save")}
            </Button>
          </div>
        </form>
      </Dialog>
    </Layout>
  );
};

export default CardInterestsList;
